import { Button, Css, PageHeaderLayout } from "@homebound/beam";
import { useCallback } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { usePlanPackageVersionHistoryQuery } from "src/routes/libraries/plan-package-v2/version-history/PlanPackageVersionHistoryPage.generated";
import { GeneralTlivChangesFromVersions } from "src/routes/libraries/versioning/GeneralTlivChanges";
import { VersionDetailsCard_VersionFragment } from "src/routes/libraries/versioning/VersionDetailsCard.generated";
import { createPlanPackagesUrl, createPlanPackageUrl } from "src/RouteUrls";
import { fail, queryResult } from "src/utils";
import { LocationChanges } from "../../versioning/LocationChanges";
import { OptionsChanges } from "../../versioning/OptionChanges";
import { ProgramDataChanges } from "../../versioning/ProgramDataChanges";
import { VersionDetailsCard } from "../../versioning/VersionDetailsCard";

type PlanPackageVersionHistoryUrlParams = { planPackageId: string };

export function PlanPackageVersionHistoryPage() {
  const { planPackageId } = useRouteParams<PlanPackageVersionHistoryUrlParams>();
  const query = usePlanPackageVersionHistoryQuery({
    variables: { filter: { readyPlanId: [planPackageId] }, page: { limit: 10 } },
  });
  const maybeFetchMore = useCallback(async () => {
    if (query.data?.readyPlanAggregateVersionsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data?.readyPlanAggregateVersionsPage.entities?.length, limit: 10 } },
      });
    }
  }, [query]);

  return queryResult(query, ({ readyPlanAggregateVersionsPage }) => (
    <PlanPackageVersionHistoryView
      versions={readyPlanAggregateVersionsPage.entities}
      hasNextPage={readyPlanAggregateVersionsPage.pageInfo.hasNextPage}
      maybeFetchMore={maybeFetchMore}
    />
  ));
}

type PlanPackageVersionHistoryViewProps = {
  versions: VersionDetailsCard_VersionFragment[];
  hasNextPage: boolean;
  maybeFetchMore: () => Promise<void>;
};

function PlanPackageVersionHistoryView(props: PlanPackageVersionHistoryViewProps) {
  const { planPackageId } = useRouteParams<PlanPackageVersionHistoryUrlParams>();
  const { versions, hasNextPage, maybeFetchMore } = props;
  // We reverse the array so that the most recent version is shown first.
  const rpavs = [...versions].reverse();
  const latestVersion = rpavs.first ?? fail("Expected at least one version");

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Version History",
        breadcrumbs: {
          breadcrumbs: [
            { href: createPlanPackagesUrl(), label: "Plans" },
            { href: createPlanPackageUrl(planPackageId, latestVersion.id), label: latestVersion.readyPlan.name },
          ],
        },
      }}
    >
      <div css={Css.px3.pb3.$}>
        <div css={Css.df.fdc.gap2.$}>
          {rpavs.reverse().map((rpav) => (
            <VersionDetailsCard
              key={rpav.id}
              version={rpav}
              changeDetails={<PlanPackageVersionChanges version={rpav} />}
              versionUrl={createPlanPackageUrl(rpav.readyPlan.id, rpav.id)}
            />
          ))}
        </div>
        {hasNextPage && (
          <div css={Css.df.jcc.mt1.mb1.p2.$}>
            <Button label="Load More" onClick={maybeFetchMore} />
          </div>
        )}
      </div>
    </PageHeaderLayout>
  );
}

type PlanPackageVersionChangesProps = { version: VersionDetailsCard_VersionFragment };

function PlanPackageVersionChanges({ version }: PlanPackageVersionChangesProps) {
  const { tliVersions } = version;
  return (
    <>
      <OptionsChanges version={version} />
      <ProgramDataChanges version={version} />
      <LocationChanges version={version} />
      <GeneralTlivChangesFromVersions tliVersions={tliVersions} />
    </>
  );
}
